import React, { ReactNode, SyntheticEvent, useState } from 'react';

import { Breadcrumbs, Skeleton, Card, CardContent, Grid, Paper, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import UserContainer from './UserContainer';
import { UserDetailActivity } from './UserDetailActivity';
import { UserDetailClasses } from './UserDetailClasses';
import { UserDetailProfile } from './UserDetailProfile';
import { IsAnyProdEnvironment } from '../../../../../utils/environment';
import AdminAuthContainer from '../../../AdminAuthContainer';
import StyledFormTitle from '../../../styles/form-title';
import { UserProvider } from '../../../UserContext';

interface TabPanelProps {
    children?: ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`class-detail-tabpanel-${index}`}
            aria-labelledby={`class-detail-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ mt: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `class-detail-tab-${index}`,
        'aria-controls': `class-detail-tabpanel-${index}`,
        value: index
    };
}

const Title = styled(StyledFormTitle)`
    margin-bottom: 1rem;
`;

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

export const UserDetail = () => {
    console.log('UserDetail');
    const { t } = useTranslation(['portal', 'create_edit_class']);
    const { userData } = useContainer(UserContainer);
    const { authMode } = useContainer(AdminAuthContainer);
    const theme = useTheme();
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event: SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    if (!userData || userData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    return (
        <>
            <Grid container justifyContent="space-between">
                <Grid item>
                    <Title>{t('portal:users.title')}</Title>
                </Grid>
            </Grid>

            <Breadcrumbs aria-label="breadcrumb">
                <Link to="/portal/users">{t('portal:all-users')}</Link>
                <Typography color="text.primary">{userData?.user_name}</Typography>
            </Breadcrumbs>

            <Paper elevation={0} sx={{ mt: 2 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="fullWidth"
                    aria-label={t('portal:classes-page.class-detail-tabs')}
                >
                    <Tab label={t('portal:users.classes')} {...a11yProps(0)} />
                    <Tab label={t('portal:users.activity')} {...a11yProps(1)} />
                    {['customerSupport'].includes(authMode) && IsAnyProdEnvironment() && (
                        <Tab label={t('portal:users.insights')} {...a11yProps(2)} />
                    )}
                </Tabs>
            </Paper>

            <UserProvider
                userId={userData.user_id as unknown as string}
                userUuid={window.localStorage?.user_uuid as string}
            >
                <TabPanel value={tabValue} index={0} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{userData && <UserDetailClasses />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={1} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{userData && <UserDetailActivity />}</CardContent>
                    </Card>
                </TabPanel>
                <TabPanel value={tabValue} index={2} dir={theme.direction}>
                    <Card elevation={0} sx={{ overflow: 'visible', padding: 0 }}>
                        <CardContent>{userData && <UserDetailProfile />}</CardContent>
                    </Card>
                </TabPanel>
            </UserProvider>
        </>
    );
};
